import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllRows } from '@/lib/supabaseHelpers';
import { FlaskConical, Info, ShoppingCart, Euro } from 'lucide-react';

interface ShopifyOrderRow {
  variant: string | null;
  total_price: number;
}

interface Session {
  ab_variant: string | null;
  is_completed: boolean | null;
  current_step: number | null;
  utm_source: string | null;
  updated_at: string;
}

interface PageView {
  ab_variant: string | null;
  utm_source: string | null;
}

const VARIANT_LABELS: Record<string, string> = {
  A: 'Zonder social proof (huidige versie)',
  B: 'Met social proof op scherm 1',
};

// === Statistical significance helpers ===

/** Standard normal CDF approximation (Abramowitz & Stegun) */
function normalCDF(z: number): number {
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
  const a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const sign = z < 0 ? -1 : 1;
  const x = Math.abs(z) / Math.SQRT2;
  const t = 1.0 / (1.0 + p * x);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return 0.5 * (1.0 + sign * y);
}

/** Two-proportion z-test */
function zTest(
  n1: number, c1: number,
  n2: number, c2: number,
  alpha = 0.05
): { z: number; pValue: number; significant: boolean; confidence: number } {
  if (n1 === 0 || n2 === 0) return { z: 0, pValue: 1, significant: false, confidence: 0 };
  const p1 = c1 / n1;
  const p2 = c2 / n2;
  const pPooled = (c1 + c2) / (n1 + n2);
  const se = Math.sqrt(pPooled * (1 - pPooled) * (1 / n1 + 1 / n2));
  if (se === 0) return { z: 0, pValue: 1, significant: false, confidence: 0 };
  const z = (p2 - p1) / se;
  const pValue = 2 * (1 - normalCDF(Math.abs(z)));
  const confidence = (1 - pValue) * 100;
  return { z, pValue, significant: pValue <= alpha, confidence };
}

/** Minimum sample size per variation (80% power, α=0.05) */
function minSampleSize(baselineRate: number, mde: number): number {
  const zAlpha = 1.96;
  const zBeta = 0.84;
  const p1 = baselineRate;
  const p2 = baselineRate + mde;
  if (p2 <= 0 || p2 >= 1 || p1 <= 0 || p1 >= 1) return 999;
  const num = Math.pow(zAlpha + zBeta, 2) * (p1 * (1 - p1) + p2 * (1 - p2));
  const den = Math.pow(p2 - p1, 2);
  return Math.ceil(num / den);
}

const LiveABTest = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [pageviews, setPageviews] = useState<PageView[]>([]);
  const [spend, setSpend] = useState<number | null>(null);
  const [loadingSpend, setLoadingSpend] = useState(true);
  const [testStartedAt, setTestStartedAt] = useState<string | null>(null);

  const [salesData, setSalesData] = useState<{ A: { orders: number; revenue: number }; B: { orders: number; revenue: number } }>({ A: { orders: 0, revenue: 0 }, B: { orders: 0, revenue: 0 } });

  const fetchData = async () => {
    // First fetch the active test's started_at to scope all data
    const { data: activeTest } = await supabase
      .from('ab_test_log')
      .select('started_at')
      .eq('status', 'active')
      .order('started_at', { ascending: false })
      .limit(1)
      .single();

    const startedAt = activeTest?.started_at || null;
    setTestStartedAt(startedAt);

    const dateFilters = startedAt
      ? [{ column: 'created_at', operator: 'gte', value: startedAt }]
      : [];

    const [sessData, pvData, ordersData] = await Promise.all([
      fetchAllRows<Session>('quiz_sessions', 'ab_variant, is_completed, current_step, utm_source, updated_at', [
        { column: 'ab_variant', operator: 'not.is', value: null },
        { column: 'utm_source', operator: 'not.is', value: null },
        ...dateFilters,
      ]),
      fetchAllRows<PageView>('quiz_pageviews', 'ab_variant, utm_source', [
        { column: 'ab_variant', operator: 'not.is', value: null },
        { column: 'utm_source', operator: 'not.is', value: null },
        ...dateFilters,
      ]),
      fetchAllRows<ShopifyOrderRow>('shopify_orders', 'variant, total_price', [
        { column: 'variant', operator: 'not.is', value: null },
        ...(startedAt ? [{ column: 'order_created_at', operator: 'gte', value: startedAt }] : []),
      ]),
    ]);
    setSessions(sessData);
    setPageviews(pvData);

    const sales = { A: { orders: 0, revenue: 0 }, B: { orders: 0, revenue: 0 } };
    ordersData.forEach(o => {
      if (o.variant === 'A') { sales.A.orders++; sales.A.revenue += Number(o.total_price); }
      else if (o.variant === 'B') { sales.B.orders++; sales.B.revenue += Number(o.total_price); }
    });
    setSalesData(sales);
  };

  const fetchSpend = async (startedAt?: string | null) => {
    setLoadingSpend(true);
    try {
      // Scope spend to the active test's start date
      const body: Record<string, string> = {};
      if (startedAt) {
        const since = startedAt.slice(0, 10); // yyyy-MM-dd
        const until = new Date().toISOString().slice(0, 10);
        body.time_range = JSON.stringify({ since, until });
      } else {
        body.date_preset = 'maximum';
      }
      const { data } = await supabase.functions.invoke('fetch-meta-ads', { body });
      const quizSpend = data?.summary?.total_spend ?? 0;
      setSpend(quizSpend);
    } catch { setSpend(0); }
    finally { setLoadingSpend(false); }
  };

  useEffect(() => {
    fetchData().then(() => {
      // fetchSpend after fetchData so we have testStartedAt
    });
    const channel = supabase
      .channel('live-ab-test')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_sessions' }, () => fetchData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_pageviews' }, () => fetchData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'shopify_orders' }, () => fetchData())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // Fetch spend scoped to test start date, refresh every 60s
  useEffect(() => {
    fetchSpend(testStartedAt);
    const spendInterval = setInterval(() => fetchSpend(testStartedAt), 60_000);
    return () => clearInterval(spendInterval);
  }, [testStartedAt]);

  const variants = new Map<string, { pageviews: number; started: number; completed: number; active: number }>();

  pageviews.forEach(pv => {
    const v = pv.ab_variant || '(geen)';
    if (!variants.has(v)) variants.set(v, { pageviews: 0, started: 0, completed: 0, active: 0 });
    variants.get(v)!.pageviews++;
  });

  const ACTIVE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
  const now = Date.now();

  sessions.forEach(s => {
    const v = s.ab_variant || '(geen)';
    if (!variants.has(v)) variants.set(v, { pageviews: 0, started: 0, completed: 0, active: 0 });
    const entry = variants.get(v)!;
    if ((s.current_step ?? 0) >= 2) entry.started++;
    if (s.is_completed) entry.completed++;
    const updatedAgo = now - new Date(s.updated_at).getTime();
    if ((s.current_step ?? 0) >= 2 && !s.is_completed && updatedAgo <= ACTIVE_THRESHOLD_MS) entry.active++;
  });

  const variantData = Array.from(variants.entries())
    .filter(([name]) => name === 'A' || name === 'B')
    .map(([name, data]) => ({ name, ...data }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const a = variantData.find(d => d.name === 'A');
  const b = variantData.find(d => d.name === 'B');

  const startTest = a && b ? zTest(a.pageviews, a.started, b.pageviews, b.started) : null;
  const cvrTest = a && b ? zTest(a.pageviews, a.completed, b.pageviews, b.completed) : null;
  const salesTest = a && b ? zTest(a.pageviews, salesData.A.orders, b.pageviews, salesData.B.orders) : null;

  const baselineStartRate = a && a.pageviews > 0 ? a.started / a.pageviews : 0.5;
  const minN = baselineStartRate > 0 ? minSampleSize(baselineStartRate, 0.05) : 0;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
        <FlaskConical className="w-4 h-4" />
        A/B Test: Social Proof Scherm 1 (V3)
        <span className="ml-auto flex items-center gap-1.5 text-xs font-medium text-emerald-600">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          LIVE
        </span>
      </h3>
      <p className="text-xs text-gray-400 mb-4">50/50 split • Two-proportion z-test • α = 0.05 (95% confidence)</p>

      <div className="space-y-4">
        {variantData.map(d => {
          const startRate = d.pageviews > 0 ? Math.round((d.started / d.pageviews) * 100) : 0;
          const cvr = d.pageviews > 0 ? Math.round((d.completed / d.pageviews) * 100) : 0;
          const bounceRate = d.pageviews > 0 ? Math.round(((d.pageviews - d.started) / d.pageviews) * 100) : 0;
          const variantSales = d.name === 'A' ? salesData.A : d.name === 'B' ? salesData.B : { orders: 0, revenue: 0 };
          const salesCvr = d.completed > 0 ? ((variantSales.orders / d.completed) * 100).toFixed(1) : '0';
          const pvToSale = d.pageviews > 0 ? ((variantSales.orders / d.pageviews) * 100).toFixed(2) : '0';

          // ROAS & CPA per variant (spend proportioneel verdeeld op basis van pageviews)
          const totalPV = (a?.pageviews || 0) + (b?.pageviews || 0);
          const variantPct = totalPV > 0 ? d.pageviews / totalPV : 0.5;
          const variantSpend = (spend ?? 0) * variantPct;
          const variantRoas = variantSpend > 0 && variantSales.revenue > 0 ? variantSales.revenue / variantSpend : null;
          const variantCpa = variantSpend > 0 && variantSales.orders > 0 ? variantSpend / variantSales.orders : null;

          return (
            <div key={d.name} className="border border-gray-100 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-sm font-bold ${d.name === 'A' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                    {d.name}
                  </span>
                  <span className="text-sm text-gray-600 max-w-xs truncate" title={VARIANT_LABELS[d.name]}>
                    "{VARIANT_LABELS[d.name]}"
                  </span>
                </div>
                <span className="text-xs text-gray-400">{d.pageviews} PV</span>
              </div>

              <div className="grid grid-cols-4 gap-3">
                <div className="text-center">
                  <p className={`text-xl font-bold ${bounceRate > 60 ? 'text-red-500' : bounceRate < 40 ? 'text-emerald-600' : 'text-gray-700'}`}>
                    {bounceRate}%
                  </p>
                  <p className="text-[10px] text-gray-400">Bounce</p>
                </div>
                <div className="text-center">
                  <p className={`text-xl font-bold ${startRate > 50 ? 'text-emerald-600' : startRate < 30 ? 'text-red-500' : 'text-blue-600'}`}>
                    {startRate}%
                  </p>
                  <p className="text-[10px] text-gray-400">Start rate</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-amber-500">
                    {d.active}
                  </p>
                  <p className="text-[10px] text-gray-400">Actief nu</p>
                </div>
                <div className="text-center">
                  <p className={`text-xl font-bold ${cvr > 30 ? 'text-emerald-600' : cvr < 20 ? 'text-red-500' : 'text-blue-600'}`}>
                    {cvr}%
                  </p>
                  <p className="text-[10px] text-gray-400">CVR</p>
                </div>
              </div>

              <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden flex">
                <div className="h-full bg-red-400 transition-all" style={{ width: `${bounceRate}%` }} title="Bounced" />
                <div className="h-full bg-blue-400 transition-all" style={{ width: `${startRate - cvr}%` }} title="Gestart maar niet voltooid" />
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${cvr}%` }} title="Voltooid" />
              </div>

              {/* Sales metrics */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-1.5 mb-2">
                  <ShoppingCart className="w-3 h-3 text-emerald-600" />
                  <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Shopify Sales</span>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-2">
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-800">{variantSales.orders}</p>
                    <p className="text-[10px] text-gray-400">Orders</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-emerald-600">€{variantSales.revenue.toFixed(2)}</p>
                    <p className="text-[10px] text-gray-400">Omzet</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-600">€{variantSpend.toFixed(2)}</p>
                    <p className="text-[10px] text-gray-400">Spend ({(variantPct * 100).toFixed(0)}%)</p>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div className="text-center">
                    <p className={`text-lg font-bold ${variantRoas !== null && variantRoas >= 1 ? 'text-emerald-600' : variantRoas !== null ? 'text-red-500' : 'text-gray-400'}`}>
                      {variantRoas !== null ? `${variantRoas.toFixed(2)}x` : '–'}
                    </p>
                    <p className="text-[10px] text-gray-400">ROAS</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-700">
                      {variantCpa !== null ? `€${variantCpa.toFixed(2)}` : '–'}
                    </p>
                    <p className="text-[10px] text-gray-400">CPA</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-blue-600">{pvToSale}%</p>
                    <p className="text-[10px] text-gray-400">PV→Sale</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-600">{salesCvr}%</p>
                    <p className="text-[10px] text-gray-400">Lead→Sale</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {variantData.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-4">Nog geen A/B test data — nieuwe sessies worden automatisch verdeeld</p>
        )}

        {/* Statistical Significance Panel */}
        {a && b && (startTest || cvrTest || salesTest) && (
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5" />
              Statistische significantie
            </h4>

            {startTest && (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Start rate</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">p = {startTest.pValue.toFixed(4)}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      startTest.significant ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {startTest.significant ? '✓ Significant' : '⏳ Niet significant'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${startTest.confidence >= 95 ? 'bg-emerald-500' : startTest.confidence >= 80 ? 'bg-amber-400' : 'bg-gray-400'}`}
                      style={{ width: `${Math.min(startTest.confidence, 100)}%` }}
                    />
                  </div>
                  <span className={`text-sm font-bold tabular-nums min-w-[3rem] text-right ${startTest.confidence >= 95 ? 'text-emerald-600' : startTest.confidence >= 80 ? 'text-amber-600' : 'text-gray-500'}`}>
                    {Math.round(startTest.confidence)}%
                  </span>
                </div>
                <p className="text-[10px] text-gray-400">z = {startTest.z.toFixed(2)} • {startTest.confidence < 95 ? `Nog ~${Math.max(0, Math.ceil((95 - startTest.confidence) / 5) * 50)} extra sessies nodig` : 'Voldoende data'}</p>
              </div>
            )}

            {salesTest && (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Sales (PV→Order)</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">p = {salesTest.pValue.toFixed(4)}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      salesTest.significant ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {salesTest.significant ? '✓ Significant' : '⏳ Niet significant'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${salesTest.confidence >= 95 ? 'bg-emerald-500' : salesTest.confidence >= 80 ? 'bg-amber-400' : 'bg-gray-400'}`}
                      style={{ width: `${Math.min(salesTest.confidence, 100)}%` }}
                    />
                  </div>
                  <span className={`text-sm font-bold tabular-nums min-w-[3rem] text-right ${salesTest.confidence >= 95 ? 'text-emerald-600' : salesTest.confidence >= 80 ? 'text-amber-600' : 'text-gray-500'}`}>
                    {Math.round(salesTest.confidence)}%
                  </span>
                </div>
                <p className="text-[10px] text-gray-400">z = {salesTest.z.toFixed(2)} • {salesTest.confidence < 95 ? `Nog ~${Math.max(0, Math.ceil((95 - salesTest.confidence) / 5) * 50)} extra sessies nodig` : 'Voldoende data'}</p>
              </div>
            )}

            {cvrTest && (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Conversie (CVR)</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">p = {cvrTest.pValue.toFixed(4)}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      cvrTest.significant ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {cvrTest.significant ? '✓ Significant' : '⏳ Niet significant'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${cvrTest.confidence >= 95 ? 'bg-emerald-500' : cvrTest.confidence >= 80 ? 'bg-amber-400' : 'bg-gray-400'}`}
                      style={{ width: `${Math.min(cvrTest.confidence, 100)}%` }}
                    />
                  </div>
                  <span className={`text-sm font-bold tabular-nums min-w-[3rem] text-right ${cvrTest.confidence >= 95 ? 'text-emerald-600' : cvrTest.confidence >= 80 ? 'text-amber-600' : 'text-gray-500'}`}>
                    {Math.round(cvrTest.confidence)}%
                  </span>
                </div>
                <p className="text-[10px] text-gray-400">z = {cvrTest.z.toFixed(2)} • {cvrTest.confidence < 95 ? `Nog ~${Math.max(0, Math.ceil((95 - cvrTest.confidence) / 5) * 50)} extra sessies nodig` : 'Voldoende data'}</p>
              </div>
            )}

            {/* Sample size guidance */}
            <div className="border-t border-gray-200 pt-2 mt-2">
              <p className="text-[11px] text-gray-500 leading-relaxed">
                📊 <strong>Sample size:</strong> ~{minN} pageviews per variant nodig om een 5pp verschil te detecteren (80% power).
                Je hebt nu <strong>{a.pageviews}</strong> (A) en <strong>{b.pageviews}</strong> (B).
                {Math.min(a.pageviews, b.pageviews) >= minN
                  ? <span className="text-emerald-600 font-medium"> ✓ Voldoende data.</span>
                  : <span className="text-amber-600 font-medium"> Nog ~{Math.max(0, minN - Math.min(a.pageviews, b.pageviews))} PV nodig.</span>
                }
              </p>
            </div>

            {/* Winner declaration */}
            {(startTest?.significant || cvrTest?.significant || salesTest?.significant) && (
              <div className="bg-white rounded-lg p-3 border border-emerald-200 text-center">
                {startTest?.significant && (
                  <p className="text-sm font-medium">
                    🏆 Variant <span className={startTest.z > 0 ? 'text-purple-700 font-bold' : 'text-blue-700 font-bold'}>{startTest.z > 0 ? 'B' : 'A'}</span> wint op start rate
                    met <span className="text-emerald-600 font-bold">
                      {Math.abs((a.pageviews > 0 ? a.started / a.pageviews : 0) * 100 - (b.pageviews > 0 ? b.started / b.pageviews : 0) * 100).toFixed(1)}pp
                    </span> verschil (p={startTest.pValue.toFixed(3)})
                  </p>
                )}
                {cvrTest?.significant && (
                  <p className="text-sm font-medium mt-1">
                    🏆 Variant <span className={cvrTest.z > 0 ? 'text-purple-700 font-bold' : 'text-blue-700 font-bold'}>{cvrTest.z > 0 ? 'B' : 'A'}</span> wint op CVR
                    met <span className="text-emerald-600 font-bold">
                      {Math.abs((a.pageviews > 0 ? a.completed / a.pageviews : 0) * 100 - (b.pageviews > 0 ? b.completed / b.pageviews : 0) * 100).toFixed(1)}pp
                    </span> verschil (p={cvrTest.pValue.toFixed(3)})
                  </p>
                )}
                {salesTest?.significant && (
                  <p className="text-sm font-medium mt-1">
                    🏆 Variant <span className={salesTest.z > 0 ? 'text-purple-700 font-bold' : 'text-blue-700 font-bold'}>{salesTest.z > 0 ? 'B' : 'A'}</span> wint op sales
                    met <span className="text-emerald-600 font-bold">
                      {Math.abs((a.pageviews > 0 ? salesData.A.orders / a.pageviews : 0) * 100 - (b.pageviews > 0 ? salesData.B.orders / b.pageviews : 0) * 100).toFixed(2)}pp
                    </span> verschil (p={salesTest.pValue.toFixed(3)})
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveABTest;